import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiFileText, FiMail, FiDownload, FiCheck } = FiIcons;

const ReportModal = ({ isOpen, onClose, onSubmit, submissionData, incentivesData }) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: Enter Info, 2: Download
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    termsAccepted: false,
    marketingOptIn: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [generatedReport, setGeneratedReport] = useState(null);
  const [reportPdfBlob, setReportPdfBlob] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim() || !formData.email.trim() || !formData.termsAccepted) {
      alert('Please fill in all required fields and accept the terms');
      return;
    }

    setIsSubmitting(true);
    setGenerationProgress(0);
    setProgressMessage('');

    try {
      const result = await onSubmit(formData, submissionData, incentivesData, (progress, message) => {
        setGenerationProgress(progress);
        setProgressMessage(message);
      });

      setGeneratedReport(result.reportContent);
      setReportPdfBlob(result.pdfBlob);
      setGenerationProgress(100);
      setProgressMessage('Report generated successfully!');
      setCurrentStep(2); // Move to download step
    } catch (error) {
      console.error('Report generation error:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = () => {
    if (reportPdfBlob) {
      const url = URL.createObjectURL(reportPdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nupitch-incentive-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetModal = () => {
    setCurrentStep(1);
    setFormData({
      fullName: '',
      email: '',
      termsAccepted: false,
      marketingOptIn: false
    });
    setGeneratedReport(null);
    setReportPdfBlob(null);
    setGenerationProgress(0);
    setProgressMessage('');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className={`bg-white rounded-2xl p-6 w-full ${generatedReport ? 'max-w-4xl max-h-[90vh] overflow-y-auto' : 'max-w-lg'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {!generatedReport ? (
              // Report Generation Form
              <>
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <SafeIcon icon={currentStep === 1 ? FiFileText : FiDownload} className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {currentStep === 1 ? 'Get Full Detailed Report' : 'Download Your Report'}
                      </h3>
                      <div className="flex items-center space-x-4">
                        <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                            1
                          </div>
                          <span className="text-sm">Enter Info</span>
                        </div>
                        <div className={`w-8 h-px ${currentStep >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`}></div>
                        <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                            2
                          </div>
                          <span className="text-sm">Download</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 focus-ring rounded p-1"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    Get a comprehensive <strong>FREE</strong> report with professional advice, detailed incentive analysis,
                    financial projections, and actionable next steps. Simply enter your name and email below to generate and download your personalised report.
                  </p>

                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <SafeIcon icon={FiMail} className="w-5 h-5 text-primary-600 mt-0.5" />
                      <div className="text-sm text-primary-800">
                        <p className="font-medium mb-1">What you'll get:</p>
                        <ul className="space-y-1 text-primary-700">
                          <li>• Detailed financial impact assessment</li>
                          <li>• Strategic recommendations for maximising benefits</li>
                          <li>• Actionable next steps and timelines</li>
                          <li>• Downloadable PDF</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                {isSubmitting && (
                  <div className="mb-6">
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${generationProgress}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full"></div>
                      <p className="text-sm text-gray-600">{progressMessage || 'Processing...'}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => handleChange('fullName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Your full name"
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="your@email.com"
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.termsAccepted}
                        onChange={(e) => handleChange('termsAccepted', e.target.checked)}
                        className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        disabled={isSubmitting}
                      />
                      <span className="text-sm text-gray-700">
                        I accept the <a href="/terms" target="_blank" className="text-primary-600 hover:underline">Terms of Service</a> and <a href="/privacy" target="_blank" className="text-primary-600 hover:underline">Privacy Policy</a> *
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.marketingOptIn}
                        onChange={(e) => handleChange('marketingOptIn', e.target.checked)}
                        className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        disabled={isSubmitting}
                      />
                      <span className="text-sm text-gray-700">
                        Send me NuPitch updates and tips (optional)
                      </span>
                    </label>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors focus-ring"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.termsAccepted}
                      className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 transition-colors focus-ring"
                    >
                      {isSubmitting ? 'Generating Report...' : 'Generate Report'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              // Generated Report Display
              <>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Report Generated Successfully!
                      </h3>
                      <p className="text-sm text-gray-600">Professional analysis by NuPitch AI</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium bg-green-600 text-white">
                          ✓
                        </div>
                        <span className="text-sm text-green-600 font-medium">Step 2: Ready to Download</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 focus-ring rounded p-1"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <button
                    onClick={handleDownload}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors focus-ring"
                  >
                    <SafeIcon icon={FiDownload} className="w-5 h-5" />
                    <span>Download PDF Report</span>
                  </button>
                </div>

                {/* Report content is now only available in PDF - no text display */}

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center">
                    This report was generated by NuPitch Incentive Match Maker AI.
                    Always verify incentive details directly with the relevant government agencies.
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportModal;